import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Edit2, Save, Percent, Tag } from 'lucide-react';
import { formatDateFor } from './adminUtils';

export const AdminPackagesTab = ({
  packagePrices, campaigns, editingPrices, setEditingPrices,
  onSavePrices, onCancelPrices, onPriceChange, showCampaignModal, setShowCampaignModal,
  campaignForm, setCampaignForm, onCreateCampaign, onDeleteCampaign, onToggleCampaign, language
}) => {
  const tr = language === 'tr';
  const formatDate = formatDateFor(language);
  return (
    <div className="space-y-6" data-testid="admin-packages-tab">
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">{tr ? 'Paket Fiyatlandırması' : 'Package Pricing'}</CardTitle>
              <CardDescription className="text-slate-400">
                {tr ? 'Paket fiyatlarını düzenleyin' : 'Edit package prices'}
              </CardDescription>
            </div>
            {editingPrices ? (
              <div className="flex space-x-2">
                <Button variant="outline" onClick={onCancelPrices} className="border-slate-700" data-testid="cancel-prices-btn">
                  {tr ? 'İptal' : 'Cancel'}
                </Button>
                <Button onClick={onSavePrices} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-prices-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {tr ? 'Kaydet' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button onClick={() => setEditingPrices(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-prices-btn">
                <Edit2 className="h-4 w-4 mr-2" />
                {tr ? 'Düzenle' : 'Edit'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400">{tr ? 'Paket' : 'Tier'}</TableHead>
                <TableHead className="text-slate-400">TRY (₺)</TableHead>
                <TableHead className="text-slate-400">USD ($)</TableHead>
                <TableHead className="text-slate-400">EUR (€)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {['executive_scan', 'operational', 'enterprise', 'strategic'].map((tier) => (
                <TableRow key={tier} className="border-slate-700">
                  <TableCell className="text-white font-medium capitalize">{tier.replace('_', ' ')}</TableCell>
                  {['TRY', 'USD', 'EUR'].map((currency) => (
                    <TableCell key={currency}>
                      {editingPrices ? (
                        <Input type="number" value={packagePrices[tier]?.[currency] || 0}
                          onChange={(e) => onPriceChange(tier, currency, e.target.value)}
                          data-testid={`price-input-${tier}-${currency}`}
                          className="bg-slate-800 border-slate-700 text-white w-24 h-8" />
                      ) : (
                        <span data-testid={`price-value-${tier}-${currency}`} className={tier === 'executive_scan' ? 'text-slate-400' : 'text-emerald-400'}>
                          {packagePrices[tier]?.[currency] || 0}
                        </span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Tag className="h-5 w-5 text-purple-400" />
              <CardTitle className="text-white">{tr ? 'Kampanyalar' : 'Campaigns'}</CardTitle>
            </div>
            <Button onClick={() => setShowCampaignModal(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="new-campaign-btn">
              <Percent className="h-4 w-4 mr-2" />
              {tr ? 'Yeni Kampanya' : 'New Campaign'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {campaigns.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">{tr ? 'Kampanya Adı' : 'Campaign Name'}</TableHead>
                  <TableHead className="text-slate-400">{tr ? 'İndirim' : 'Discount'}</TableHead>
                  <TableHead className="text-slate-400">{tr ? 'Paket' : 'Tier'}</TableHead>
                  <TableHead className="text-slate-400">{tr ? 'Başlangıç' : 'Start'}</TableHead>
                  <TableHead className="text-slate-400">{tr ? 'Bitiş' : 'End'}</TableHead>
                  <TableHead className="text-slate-400">{tr ? 'Durum' : 'Status'}</TableHead>
                  <TableHead className="text-slate-400">{tr ? 'İşlem' : 'Action'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id} className="border-slate-700">
                    <TableCell className="text-white">{c.name}</TableCell>
                    <TableCell className="text-emerald-400">{c.discount}%</TableCell>
                    <TableCell className="text-slate-300 capitalize">{c.tier?.replace('_', ' ') || (tr ? 'Tümü' : 'All')}</TableCell>
                    <TableCell className="text-slate-400">{formatDate(c.start_date)}</TableCell>
                    <TableCell className="text-slate-400">{formatDate(c.end_date)}</TableCell>
                    <TableCell>
                      <button onClick={() => onToggleCampaign(c.id)}
                        className={`px-2 py-1 text-xs rounded-full cursor-pointer ${c.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                        {c.active ? (tr ? 'Aktif' : 'Active') : (tr ? 'Pasif' : 'Inactive')}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => onDeleteCampaign(c.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Tag className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">{tr ? 'Henüz kampanya yok' : 'No campaigns yet'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {showCampaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="campaign-modal">
          <Card className="bg-slate-900 border-slate-700 w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-white">{tr ? 'Yeni Kampanya Oluştur' : 'Create New Campaign'}</CardTitle>
              <CardDescription className="text-slate-400">
                {tr ? 'İndirim kampanyası bilgilerini girin' : 'Enter discount campaign details'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">{tr ? 'Kampanya Adı' : 'Campaign Name'}</Label>
                <Input value={campaignForm.name}
                  onChange={(e) => setCampaignForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder={tr ? 'Ör: Yılbaşı Kampanyası' : 'E.g.: New Year Campaign'}
                  className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="campaign-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{tr ? 'İndirim (%)' : 'Discount (%)'}</Label>
                  <Input type="number" min={1} max={100} value={campaignForm.discount}
                    onChange={(e) => setCampaignForm((p) => ({ ...p, discount: parseInt(e.target.value) || 0 }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="campaign-discount-input" />
                </div>
                <div>
                  <Label className="text-slate-300">{tr ? 'Paket' : 'Tier'}</Label>
                  <Select value={campaignForm.tier} onValueChange={(v) => setCampaignForm((p) => ({ ...p, tier: v }))}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue placeholder={tr ? 'Tümü' : 'All'} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all" className="text-white">{tr ? 'Tüm Paketler' : 'All Tiers'}</SelectItem>
                      <SelectItem value="operational" className="text-white">Operational</SelectItem>
                      <SelectItem value="enterprise" className="text-white">Enterprise</SelectItem>
                      <SelectItem value="strategic" className="text-white">Strategic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{tr ? 'Başlangıç Tarihi' : 'Start Date'}</Label>
                  <Input type="date" value={campaignForm.start_date}
                    onChange={(e) => setCampaignForm((p) => ({ ...p, start_date: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="campaign-start-input" />
                </div>
                <div>
                  <Label className="text-slate-300">{tr ? 'Bitiş Tarihi' : 'End Date'}</Label>
                  <Input type="date" value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm((p) => ({ ...p, end_date: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="campaign-end-input" />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={() => setShowCampaignModal(false)} className="border-slate-700 text-slate-300">
                  {tr ? 'İptal' : 'Cancel'}
                </Button>
                <Button onClick={onCreateCampaign} className="bg-purple-600 hover:bg-purple-700" data-testid="campaign-submit-btn">
                  {tr ? 'Oluştur' : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
